import { createColumnHelper } from '@tanstack/react-table';
import { TableLink } from '@monorepo/base/src/components/table/table-link/table-link';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import routes from '../../routes/index.routes';
import { IMergedHeadersReturn } from './index.headers';
import { IAdminxStore } from '../../stores';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useEffect } from 'react';
import { statusCell, currencyCellCreator, defaultMetricCellCreator } from './cells/cells';
import { TableMergeColumn } from '@monorepo/base/src/components/table/table-merge-column/table-merge-column';
import { TableSelectionCell, TableSelectionHeader } from '@monorepo/base/src/components/table/table-selection-cell/table-selection-cell';
import { SpacerX } from '@monorepo/base/src/components/spacer/spacer';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { Statuses } from '../../enums/status';
import { isSubRow } from '@monorepo/base/src/components/table/table';
import { TableStatusCell } from '../../components/table-status-cell/table-status-cell';
import { BidderAdvertiserTableModel } from '../../models/bidder-advertiser.model';

const columnHelper = createColumnHelper<BidderAdvertiserTableModel>();

export const useBidderAdvertisersHeaders = (): IMergedHeadersReturn<BidderAdvertiserTableModel> => {
	const { settingsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;

	useEffect(() => {
		if (tableStore && Object.keys(tableStore.getColumnVisibility()).length === 0) {
			tableStore.setDefaultColumnVisibility({
				id: true,
				name: true,
				status: true,
				dailyMoneySpent: true,
				dailyMoneyEarn: true,
				totalMoneySpent: true,
				totalMoneyEarn: true,
				totalBudget: true,
				priority: true,
				shouldReceiveL1: true,
				useEnhancedBlackList: true,
			});

			tableStore?.setColumnVisibility(tableStore.getDefaultColumnVisibility());

			if (tableStore.getSortingBy().length === 0) {
				tableStore.setSortingBy([
					{
						id: 'id',
						desc: false,
					},
				]);
			}
		}
	}, [tableStore]);

	return {
		columns: [
			columnHelper.display({
				id: 'selection',
				enableHiding: false,
				header: headerProps => (
					<TableMergeColumn>
						<TableSelectionHeader headerProps={headerProps} />
						<SpacerX x={0.7} />
						<Icon size={'14px'} isMFP={true} color={'#c5c5c5'}>
							circle
						</Icon>
						<SpacerX x={0.7} />
					</TableMergeColumn>
				),
				cell: cellProps => {
					const { row } = cellProps;
					const campaign = new BidderAdvertiserTableModel(row.original);

					if (isSubRow(row)) {
						return <span></span>;
					}

					return (
						<TableMergeColumn>
							<TableSelectionCell cellProps={cellProps} />
							<SpacerX x={0.7} />
							<TableStatusCell status={campaign.status as Statuses} />
						</TableMergeColumn>
					);
				},
			}),

			columnHelper.accessor('id', { header: 'ID' }),
			columnHelper.accessor('name', {
				header: 'Name',
				cell: ({ row }) => {
					// const note = row?.original?.note;

					return (
						<TableLink
							toEdit={routes.bidderAdvertisers.editAdvertiser(row.getValue('id'))}
							toEditState={{ isBlockRequest: true } as BetweenPagesStateProps}>
							{row.getValue('name')}

							{/* {!!note && (
								<div style={{ marginLeft: 4 }}>
									<Tooltip content={note}>
										<Icon isMFP color="red">
											info-circle
										</Icon>
									</Tooltip>
								</div>
							)} */}
						</TableLink>
					);
				},
			}),
			columnHelper.accessor('status', {
				header: 'Status',
				cell: statusCell,
			}),
			columnHelper.accessor('dailyMoneySpent', {
				header: 'Daily Money Spent',
				cell: currencyCellCreator<BidderAdvertiserTableModel>('dailyMoneySpent'),
			}),
			columnHelper.accessor('dailyMoneyEarn', {
				header: 'Daily Money Earn',
				cell: currencyCellCreator<BidderAdvertiserTableModel>('dailyMoneyEarn'),
			}),
			columnHelper.accessor('totalBudget', {
				header: 'Total Budget',
				cell: currencyCellCreator<BidderAdvertiserTableModel>('totalBudget'),
			}),
			columnHelper.accessor('totalMoneySpent', {
				header: 'Total Money Spent',
				cell: currencyCellCreator<BidderAdvertiserTableModel>('totalMoneySpent'),
			}),
			columnHelper.accessor('totalMoneyEarn', {
				header: 'Total Money Earn',
				cell: currencyCellCreator<BidderAdvertiserTableModel>('totalMoneyEarn'),
			}),
			columnHelper.accessor('priority', {
				header: 'Priority',
				cell: defaultMetricCellCreator<BidderAdvertiserTableModel>('priority'),
			}),
			columnHelper.accessor('shouldReceiveL1', {
				header: 'Should Receive L1',
				cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
			}),
			columnHelper.accessor('useEnhancedBlackList', {
				header: 'Use Enhanced Black List',
				cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
			}),
		],
	};
};
